import 'setimmediate'

import React, { useEffect, useState } from 'react'
import { ActivityIndicator, PixelRatio, Platform } from 'react-native'

import { env } from '@env'
import { QueryClient } from '@tanstack/react-query'
import { LinkBroken } from '@zet/common/components/linkBroken'
import { useIsSsoLoggedInUser } from '@zet/common/hooks/useIsSsoLoggedInUser'
import { Stack } from '@zet/design-system'
import { setNativewindColors } from '@zet/design-system/tailwind/utils'
import '@zet/utils/freshchat'
import storageUtil from '@zet/utils/storage'
import { useFonts } from 'expo-font'
import { StatusBar } from 'expo-status-bar'
import { NativeWindStyleSheet } from 'nativewind'
import 'react-native-gesture-handler'
import { enableExperimentalWebImplementation } from 'react-native-gesture-handler'

import { bridgeApi } from './api/network'
import { i18n } from './localization'
import Root from './navigation/Root'
import AppProviders from './providers/AppProviders'
import useAuthStore from './store/authStore'
import { CATEGORIES_MAPPING } from './utils/Constants'

enableExperimentalWebImplementation(true)

const clearStorage = () => {
  storageUtil.deleteObject('token')
  storageUtil.deleteObject('category')
  storageUtil.deleteObject('isSsoLoggedInUser')
  storageUtil.deleteObject('parentAgentPhoneNumber')
  storageUtil.deleteObject('helplineNumber')
  storageUtil.deleteObject('dsaLogo')
  storageUtil.deleteObject('--color-primary-100')
  storageUtil.deleteObject('--color-primary-150')
  storageUtil.deleteObject('--color-secondary-100')
  storageUtil.deleteObject('--color-secondary-150')
}

async function getClientDetails(hash: string) {
  try {
    const response = await fetch(
      env.API_BASE_URL + 'enterprise/subAgent/client/details',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hash: encodeURIComponent(hash),
        }),
      }
    )

    const data = await response.json()

    storageUtil.saveObject('token', data?.data?.jwtToken)
    storageUtil.saveObject('isSsoLoggedInUser', true)
    storageUtil.saveObject(
      'parentAgentPhoneNumber',
      data?.data?.phoneNumber || ''
    )
    storageUtil.saveObject('helplineNumber', data?.data?.helplineNumber || '')

    bridgeApi.defaults.headers.common = {
      ...bridgeApi.defaults.headers.common,
      Authorization: `Bearer ${data.data.jwtToken}`,
      Accept: 'application/json',
      locale: i18n.locale,
    }

    if (
      data?.data?.metaData?.colors?.primary &&
      (data?.data?.metaData?.colors?.primary?.length === 4 ||
        data?.data?.metaData?.colors?.primary?.length === 7) &&
      data?.data?.metaData?.colors?.primary !== '#ffffff'
    ) {
      storageUtil.saveObject(
        '--color-primary-100',
        data?.data?.metaData?.colors?.primary
      )
      setNativewindColors({
        '--color-primary-100': `${data?.data?.metaData?.colors?.primary}`,
      })

      storageUtil.saveObject(
        '--color-primary-150',
        data?.data?.metaData?.colors?.primary
      )
      setNativewindColors({
        '--color-primary-150': `${data?.data?.metaData?.colors?.primary}`,
      })
    }

    if (
      data?.data?.metaData?.colors?.secondary &&
      (data?.data?.metaData?.colors?.secondary?.length === 4 ||
        data?.data?.metaData?.colors?.secondary?.length === 7) &&
      data?.data?.metaData?.colors?.secondary !== '#ffffff'
    ) {
      storageUtil.saveObject(
        '--color-secondary-100',
        data?.data?.metaData?.colors?.secondary
      )
      setNativewindColors({
        '--color-secondary-100': `${data?.data?.metaData?.colors?.secondary}`,
      })

      storageUtil.saveObject(
        '--color-secondary-150',
        data?.data?.metaData?.colors?.secondary
      )
      setNativewindColors({
        '--color-secondary-150': `${data?.data?.metaData?.colors?.secondary}`,
      })
    }

    if (data?.data?.metaData?.returnUrl) {
      storageUtil.saveObject('returnUrl', data?.data?.metaData?.returnUrl)
    }

    if (data?.data?.metaData?.icon) {
      storageUtil.saveObject('dsaLogo', data?.data?.metaData?.icon)
    }

    return 'SUCCESS'
  } catch (error) {
    console.log(error)
    clearStorage()
    return 'FAILURE'
  }
}

const params = new URLSearchParams(window.location.search)

setNativewindColors({
  '--color-primary-100': '#2075F0',
  '--color-primary-150': '#145CC5',
  '--color-secondary-100': '#5FAFFF',
  '--color-secondary-150': '#389CFF',
})

export default function App() {
  const [isLoading, setIsLoading] = useState(true)
  const [isHashInValid, setIsHashInValid] = useState(false)
  const { isSsoLoggedInUser } = useIsSsoLoggedInUser()
  const { setIsAuthenticated, unauthorizedApiError } = useAuthStore(
    (state) => state
  )

  if (Platform.OS === 'web') {
    ;(window as any)._frameTimestamp = null
  }

  if (PixelRatio.get() < 2) {
    NativeWindStyleSheet.setVariables({ '--rem': 16 })
  }

  useFonts({
    'Graphie-Bold': require('../assets/fonts/Graphie/Graphie-Bold.otf'),
    'Graphie-Book': require('../assets/fonts/Graphie/Graphie-Book.otf'),
    'Graphie-Regular': require('../assets/fonts/Graphie/Graphie-Regular.otf'),
    'Graphie-SemiBold': require('../assets/fonts/Graphie/Graphie-SemiBold.otf'),
  })

  useEffect(() => {
    const hash = params.get('hash')
    const category = params.get('category')

    async function prepare() {
      if (hash) {
        clearStorage()

        const result = await getClientDetails(hash)
        if (result === 'SUCCESS') {
          setIsAuthenticated(true)
          setIsLoading(false)
          return
        } else {
          setIsLoading(false)
          setIsHashInValid(true)
          return
        }
      }

      let token = storageUtil.getObject('token')

      const colorPrimary100 = storageUtil.getObject('--color-primary-100')
      const colorPrimary150 = storageUtil.getObject('--color-primary-150')
      const colorSecondary100 = storageUtil.getObject('--color-secondary-100')
      const colorSecondary150 = storageUtil.getObject('--color-secondary-150')

      colorPrimary100 &&
        setNativewindColors({
          '--color-primary-100': `${colorPrimary100}`,
        })

      colorSecondary100 &&
        setNativewindColors({
          '--color-secondary-100': `${colorSecondary100}`,
        })

      colorPrimary150 &&
        setNativewindColors({
          '--color-primary-150': `${colorPrimary150}`,
        })

      colorSecondary150 &&
        setNativewindColors({
          '--color-secondary-150': `${colorSecondary150}`,
        })

      token = token ? `Bearer ${token}` : null
      if (token) {
        bridgeApi.defaults.headers.common = {
          ...bridgeApi.defaults.headers.common,
          Authorization: token,
          Accept: 'application/json',
          locale: i18n.locale,
        }
        setIsAuthenticated(true)
      }
      setIsLoading(false)
    }
    prepare()

    // To check if the user is logged in via SSO and Category is passed
    if (category && hash) {
      storageUtil.saveObject('category', CATEGORIES_MAPPING[category])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (unauthorizedApiError) {
      setIsAuthenticated(false)
      if (isSsoLoggedInUser) {
        clearStorage()
        setIsHashInValid(true)
      } else {
        clearStorage()
        window.location.href = '/verify-phone-number'
      }
    }
  }, [isSsoLoggedInUser, setIsAuthenticated, unauthorizedApiError])

  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // cacheTime: 1000 * 60 * 60 * 24 * 7,
        // staleTime: 1000 * 60 * 60 * 24 * 7,
      },
    },
  })

  const onLayoutRootView = () => null

  if (isLoading)
    return (
      <Stack className="w-full items-center justify-center">
        <ActivityIndicator />
      </Stack>
    )

  if (isHashInValid) return <LinkBroken />

  return (
    <AppProviders onLayout={onLayoutRootView} queryClient={client}>
      <Root />
      <StatusBar style={'dark'} />
    </AppProviders>
  )
}
